import { DBricksConnection } from '../workflow/connections/databricks'
import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  ComponentInputOutputSchemas,
  Language,
  Provider,
  QueryAndOutputs,
  TableOptimizationColumn,
  TableOptimizationColumnDataType,
  TableOptimizationInfo,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentOutputType,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowParameterValueError,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { WorkflowComponent } from './common/workflowcomponent'
import { SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'

export const OPTIMIZATION_COLUMN_PARAMETER_NAME = 'optimizationcol'
export const SF_SEARCH_OPTIMIZATION_COLUMN_PARAMETER_NAME = 'sfsearchoptimizationcol'
export const PG_CLUSTERING_PARAMETER_NAME = 'pgclustering'

const REPLACE_TEMPLATE = '####'

class SaveAsTable extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.saveastable',
        group: 'Input/Output',
        title: 'Save as Table',
        description: `
      This component creates a permanent table in the specified destination.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'destination',
          type: WorkflowComponentParamType.OutputTable,
          title: 'Table details',
          placeholder: 'Rename and select destination'
        },
        {
          name: 'append',
          type: WorkflowComponentParamType.Boolean,
          title: 'Append to existing table',
          default: false
        },
        {
          name: OPTIMIZATION_COLUMN_PARAMETER_NAME,
          type: WorkflowComponentParamType.Column,
          title: 'Cluster by',
          parent: 'source',
          dataType: [
            ColumnDataType.Geography,
            ColumnDataType.Geometry,
            ColumnDataType.Boolean,
            ColumnDataType.Number,
            ColumnDataType.String,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Timestamp
          ],
          providers: [Provider.BigQuery],
          optional: true,
          advanced: true
        },
        {
          name: SF_SEARCH_OPTIMIZATION_COLUMN_PARAMETER_NAME,
          type: WorkflowComponentParamType.Column,
          title: 'Search optimization column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry],
          providers: [Provider.Snowflake],
          optional: true,
          advanced: true
        },
        {
          name: OPTIMIZATION_COLUMN_PARAMETER_NAME,
          type: WorkflowComponentParamType.Column,
          title: 'Cluster by',
          parent: 'source',
          dataType: [ColumnDataType.Boolean, ColumnDataType.Number, ColumnDataType.String],
          providers: [Provider.Snowflake],
          optional: true,
          advanced: true
        },
        {
          name: OPTIMIZATION_COLUMN_PARAMETER_NAME,
          type: WorkflowComponentParamType.Column,
          title: 'Sortkey',
          parent: 'source',
          dataType: [ColumnDataType.Boolean, ColumnDataType.Number, ColumnDataType.String],
          providers: [Provider.Redshift],
          optional: true,
          advanced: true
        },
        {
          name: OPTIMIZATION_COLUMN_PARAMETER_NAME,
          type: WorkflowComponentParamType.Column,
          title: 'Index column',
          parent: 'source',
          dataType: [
            ColumnDataType.Geography,
            ColumnDataType.Geometry,
            ColumnDataType.Boolean,
            ColumnDataType.Number,
            ColumnDataType.String
          ],
          providers: [Provider.Postgres],
          optional: true,
          advanced: true
        },
        {
          name: PG_CLUSTERING_PARAMETER_NAME,
          type: WorkflowComponentParamType.Boolean,
          title: 'Cluster by index',
          parent: 'source',
          providers: [Provider.Postgres],
          default: false,
          advanced: true
        }
      ],
      [
        {
          name: SINGLE_OUTPUT_NAME,
          title: 'Result view',
          description: 'Result view',
          type: WorkflowComponentOutputType.View
        }
      ]
    )
  }

  checkInputValues({ params, resolvedParamValues, dryRun }: WorkflowComponentCheckInputValuesParams) {
    const problems: Array<WorkflowParameterValueError> = []
    if (!dryRun) {
      if (params.destination && !isExpressionValue(params.destination as WorkflowComponentValue)) {
        const value = (params.destination as WorkflowComponentValue).value
        if (value) {
          const tokens = (value as string).split('.')
          if (tokens.length !== 3) {
            problems.push({
              parameter: 'destination',
              type: WorkflowParameterValueErrorType.WrongValue,
              message: 'Destination must be a fully qualified tablename'
            })
          }
        } else {
          problems.push({
            parameter: 'destination',
            type: WorkflowParameterValueErrorType.MissingValue,
            message: 'No destination tablename provided'
          })
        }
      }
    }
    if (!resolvedParamValues?.source) {
      problems.push({
        parameter: 'source',
        type: WorkflowParameterValueErrorType.MissingValue,
        message: 'No input table specified'
      })
    }
    return problems
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    let destination: string
    let query: string
    const dryRun = codeType === WorkflowCodeType.DryRun
    const preparedParams = this._prepareParameters(params)
    const viewname = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    if (dryRun) {
      const createTable =
        connection.type === Provider.DatabricksRest
          ? connection.getCreateTempTableQuery(
              viewname,
              `SELECT /*+ REPARTITION(40) */ * FROM ${connection.quoteName(preparedParams.source as string)}`,
              codeType,
              null
            )
          : connection.getCreateTempTableQuery(
              viewname,
              `SELECT * FROM ${connection.quoteName(preparedParams.source as string)}`,
              codeType,
              null
            )
      const dropTable = connection.getDropTableQuery(viewname)
      query = dedent`${dropTable}
        ${createTable}`
      if (connection.type === Provider.DatabricksRest) {
        query = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(query)
      }
    } else {
      if (codeType === WorkflowCodeType.FullNoCache && params.append) {
        // we have to override the codeType to Full to avoid the table being deleted
        codeType = WorkflowCodeType.Full
      }
      const destinationParam = (workflow.componentValues[componentName].destination as WorkflowComponentValue)
        .value as string

      if (isExpressionValue(destinationParam)) {
        destination = preparedParams.destination as string
      } else {
        if (connection.type === Provider.Oracle) {
          destination = `'${connection.quoteName(preparedParams.destination as string)}'`
        } else {
          destination = `'${preparedParams.destination}'`
        }
      }
      let destinationVariable
      switch (connection.type) {
        case Provider.BigQuery:
          destinationVariable = dedent`
            DECLARE __destination STRING DEFAULT ${destination};
            BEGIN
          `
          break
        case Provider.DatabricksRest:
          destinationVariable = dedent`
            __destination = ${destination};
          `
          break
        case Provider.Postgres:
          destinationVariable = dedent`
            DECLARE
              __destination TEXT DEFAULT ${destination};
            BEGIN
          `
          break
        case Provider.Redshift:
          destinationVariable = dedent`
            DECLARE
              __destination VARCHAR(MAX) DEFAULT ${destination};
            BEGIN
          `
          break
        case Provider.Snowflake:
          destinationVariable = dedent`
            DECLARE
              __destination STRING DEFAULT ${destination};
            BEGIN
          `
          break
        case Provider.Oracle:
          destinationVariable = dedent`
            DECLARE
              destination VARCHAR2(4000) DEFAULT ${destination};
            BEGIN
          `
          break
      }
      if (params.append) {
        let createTable =
          connection.type === Provider.DatabricksRest
            ? connection.getCreateTableQuery(
                REPLACE_TEMPLATE,
                `SELECT /*+ REPARTITION(40) */ * FROM ${connection.quoteName(
                  preparedParams.source as string
                )} WHERE 1=0`,
                codeType,
                null,
                false
              )
            : connection.getCreateTableQuery(
                REPLACE_TEMPLATE,
                `SELECT * FROM ${connection.quoteName(preparedParams.source as string)} WHERE 1=0`,
                codeType,
                null,
                false
              )
        let insertRows =
          connection.type === Provider.DatabricksRest
            ? dedent`
              INSERT INTO ${connection.quoteName(REPLACE_TEMPLATE)}
              SELECT /*+ REPARTITION(40) */ * FROM ${connection.quoteName(preparedParams.source as string)};`
            : dedent`
              INSERT INTO ${connection.quoteName(REPLACE_TEMPLATE)}
              SELECT * FROM ${connection.quoteName(preparedParams.source as string)};`
        if (connection.type === Provider.DatabricksRest) {
          if (!dryRun) {
            insertRows += connection.optimizationStatsQuery(REPLACE_TEMPLATE)
          }
          insertRows = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
            insertRows.replace(/####/g, `''' + __destination + '''`)
          )
          createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
            createTable.replace(/####/g, `''' + __destination + '''`)
          )
        } else if (connection.type === Provider.Oracle) {
          // Oracle returns complete BEGIN...EXECUTE IMMEDIATE...END blocks with q'[...]' quoting
          // We need to rewrite them to use concatenation with __destination variable
          // Extract the SQL from inside the q'[...]' and reconstruct with concatenation
          const createTableMatch = createTable.match(/q'\[([\s\S]*?)\]'/)
          if (createTableMatch) {
            const innerSQL = createTableMatch[1].replace(
              connection.quoteName(REPLACE_TEMPLATE),
              `' || destination || '`
            )
            createTable = dedent`
              BEGIN
                EXECUTE IMMEDIATE '${innerSQL.trim().replace(/\n/g, ' ')}';
              EXCEPTION
                WHEN OTHERS THEN
                  IF SQLCODE = -955 THEN
                    NULL; -- Table already exists, do nothing
                  ELSE
                    RAISE;
                  END IF;
              END;`
          }
          insertRows = dedent`
            BEGIN
              EXECUTE IMMEDIATE 'INSERT INTO ' || destination || ' SELECT * FROM ${connection.quoteName(
                preparedParams.source as string
              )}';
            END;`
        } else {
          createTable = connection.executeDynamicSQL({
            sql: createTable,
            addQuotes: true,
            replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
          })
          insertRows = connection.executeDynamicSQL({
            sql: insertRows,
            addQuotes: true,
            replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
          })
          if ([Provider.Redshift, Provider.Postgres].includes(connection.type)) {
            let analyzeTable = `ANALYZE ${connection.quoteName(REPLACE_TEMPLATE)};`
            analyzeTable = connection.executeDynamicSQL({
              sql: analyzeTable,
              addQuotes: true,
              replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
            })
            insertRows += `\n${analyzeTable}`
          }
        }

        createTable = connection.wrapCodeInIfTableExistsBlock(createTable, [], codeType, ['__destination'])

        // Create view to point to the destination table
        let createView
        if (codeType === WorkflowCodeType.StoredProcedure) {
          createView = connection.getCreateTempTableQuery(
            viewname,
            `SELECT * FROM ${connection.quoteName(REPLACE_TEMPLATE)}`,
            codeType,
            null
          )
        } else {
          createView = connection.getCreateTempViewQuery(
            viewname,
            `SELECT * FROM ${connection.quoteName(REPLACE_TEMPLATE)}`,
            codeType
          )
        }
        if (connection.type === Provider.Oracle) {
          // Oracle returns complete BEGIN...EXECUTE IMMEDIATE...END blocks with q'[...]'
          // Extract SQL and reconstruct with concatenation
          const createViewMatch = createView.match(/q'\[([\s\S]*?)\]'/)
          if (createViewMatch) {
            const innerSQL = createViewMatch[1].replace(connection.quoteName(REPLACE_TEMPLATE), `' || destination || '`)
            createView = dedent`
              BEGIN
                EXECUTE IMMEDIATE '${innerSQL.trim().replace(/\n/g, ' ')}';
              EXCEPTION
                WHEN OTHERS THEN
                  IF SQLCODE = -955 THEN
                    NULL; -- Table already exists, do nothing
                  ELSE
                    RAISE;
                  END IF;
              END;`
          }
        } else {
          createView = connection.executeDynamicSQL({
            sql: createView,
            addQuotes: true,
            replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
          })
        }

        query = dedent`
          ${destinationVariable}
          ${createTable}
          ${insertRows}
          ${createView}
          ${connection.type != Provider.DatabricksRest ? 'END;' : ''}`
      } else {
        const optimizationInfo = this.optimizationInfo(preparedParams, connection.type, schemas)
        const quotedSource = connection.quoteName(preparedParams.source as string)

        let cloneTable = false
        if (!optimizationInfo && connection.type === Provider.BigQuery) {
          // Check if the source is a view - cannot clone views in BigQuery
          const outputTables = workflow.getOutputTables(connection, codeType)
          let isSourceView = false

          // Find which component and output corresponds to the source table
          for (const [componentName, tables] of Object.entries(outputTables)) {
            for (const [outputName, tableName] of Object.entries(tables)) {
              if (connection.unquoteName(tableName) === connection.unquoteName(preparedParams.source as string)) {
                const output = workflow.components[componentName].outputs.find((x) => x.name === outputName)
                if (output?.type === WorkflowComponentOutputType.View) {
                  isSourceView = true
                  break
                }
              }
            }
            if (isSourceView) break
          }
          cloneTable = !isSourceView
        }

        let createTable
        if (cloneTable) {
          // In BigQuery, when there is no append and no optimization,
          // clone the input table to preserve the row order
          createTable = connection.getCloneTableQuery(REPLACE_TEMPLATE, quotedSource, codeType)
        } else {
          const selectQuery =
            connection.type === Provider.DatabricksRest
              ? `SELECT /*+ REPARTITION(40) */ * FROM ${quotedSource}`
              : `SELECT * FROM ${quotedSource}`
          createTable = connection.getCreateTableQuery(REPLACE_TEMPLATE, selectQuery, codeType, optimizationInfo, true)
        }

        let dropTable = connection.getDropTableQuery(REPLACE_TEMPLATE)

        if (connection.type === Provider.DatabricksRest) {
          createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
            createTable.replace(/####/g, `''' + __destination + '''`)
          )
          dropTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
            dropTable.replace(/####/g, `''' + __destination + '''`)
          )
        } else if (connection.type === Provider.Snowflake) {
          let sentences = createTable
            .replace(/BEGIN([\s\S]*?)EXCEPTION WHEN OTHER THEN[\s\S]*?END;/, '$1')
            .split(';')
            .filter((s) => s.trim() !== '')
          sentences = sentences.map((s) =>
            connection.executeDynamicSQL({
              sql: s,
              addQuotes: true,
              replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
            })
          )
          createTable = sentences
            .map((s) => {
              if (s.includes('ALTER TABLE')) {
                return dedent`
                      BEGIN
                        ${s}
                      EXCEPTION WHEN OTHER THEN
                        NULL;
                      END;`
              }
              return s
            })
            .join('\n')
          dropTable = connection.executeDynamicSQL({
            sql: dropTable,
            addQuotes: true,
            replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
          })
        } else if (connection.type === Provider.Oracle) {
          // Oracle returns complete BEGIN...EXECUTE IMMEDIATE...END blocks with q'[...]' or regular quoting
          // We need to rewrite them to use concatenation with __destination variable

          // Handle createTable - extract SQL from q'[...]' if present
          const createTableMatch = createTable.match(/q'\[([\s\S]*?)\]'/)
          if (createTableMatch) {
            const innerSQL = createTableMatch[1].replace(
              connection.quoteName(REPLACE_TEMPLATE),
              `' || destination || '`
            )
            createTable = dedent`
              BEGIN
                EXECUTE IMMEDIATE '${innerSQL.trim().replace(/\n/g, ' ')}';
              EXCEPTION
                WHEN OTHERS THEN
                  IF SQLCODE = -955 THEN
                    NULL; -- Table already exists, do nothing
                  ELSE
                    RAISE;
                  END IF;
              END;`
          }

          // Handle dropTable - it uses regular string not q'[...]'
          dropTable = dropTable.replace(
            new RegExp(`'DROP TABLE ${connection.quoteName(REPLACE_TEMPLATE)}'`, 'g'),
            `'DROP TABLE ' || destination`
          )
        } else {
          createTable = connection.executeDynamicSQL({
            sql: createTable,
            addQuotes: true,
            replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
          })
          dropTable = connection.executeDynamicSQL({
            sql: dropTable,
            addQuotes: true,
            replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
          })
        }

        // Create view to point to the destination table
        let createView
        if (codeType === WorkflowCodeType.StoredProcedure) {
          createView = connection.getCreateTempTableQuery(
            viewname,
            `SELECT * FROM ${connection.quoteName(REPLACE_TEMPLATE)}`,
            codeType,
            null
          )
        } else {
          createView = connection.getCreateTempViewQuery(
            viewname,
            `SELECT * FROM ${connection.quoteName(REPLACE_TEMPLATE)}`,
            codeType
          )
        }
        if (connection.type === Provider.Oracle) {
          // Oracle returns complete BEGIN...EXECUTE IMMEDIATE...END blocks with q'[...]'
          // Extract SQL and reconstruct with concatenation
          const createViewMatch = createView.match(/q'\[([\s\S]*?)\]'/)
          if (createViewMatch) {
            const innerSQL = createViewMatch[1].replace(connection.quoteName(REPLACE_TEMPLATE), `' || destination || '`)
            createView = dedent`
              BEGIN
                EXECUTE IMMEDIATE '${innerSQL.trim().replace(/\n/g, ' ')}';
              EXCEPTION
                WHEN OTHERS THEN
                  IF SQLCODE = -955 THEN
                    NULL; -- Table already exists, do nothing
                  ELSE
                    RAISE;
                  END IF;
              END;`
          }
        } else {
          createView = connection.executeDynamicSQL({
            sql: createView,
            addQuotes: true,
            replacements: [[`'${REPLACE_TEMPLATE}'`, '__destination']]
          })
        }

        query = dedent`
          ${destinationVariable}
          ${dropTable}
          ${createTable}
          ${createView}
          ${connection.type != Provider.DatabricksRest ? 'END;' : ''}`
      }
    }
    return {
      query: query,
      outputs: {
        [SINGLE_OUTPUT_NAME]: connection.applyCapitalization(viewname)
      }
    }
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const viewname = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    return {
      [SINGLE_OUTPUT_NAME]: connection.applyCapitalization(viewname)
    }
  }

  language(provider: Provider): Language {
    return provider === Provider.DatabricksRest ? Language.Python : Language.SQL
  }

  optimizationInfo(
    params: Record<string, unknown>,
    provider: Provider,
    schemas?: ComponentInputOutputSchemas
  ): TableOptimizationInfo | null {
    const clusterCol = params[OPTIMIZATION_COLUMN_PARAMETER_NAME] as string
    let optimizationCol: TableOptimizationColumn | null = null
    if (schemas && clusterCol) {
      let col
      try {
        col = (Object.values(schemas.input)[0] as TableSchema).filter((x) => x.name === clusterCol)?.[0]
      } catch (e) {
        col = null
      }
      if (col) {
        optimizationCol = {
          name: col.name,
          type: [ColumnDataType.Geography, ColumnDataType.Geometry].includes(col.type)
            ? TableOptimizationColumnDataType.Geo
            : TableOptimizationColumnDataType.NonGeo
        }
      }
    }
    if (!optimizationCol && clusterCol) {
      // make an educated guess about the data type if no schemas are provided
      if (clusterCol.toLowerCase().startsWith('geo')) {
        optimizationCol = {
          name: clusterCol,
          type: TableOptimizationColumnDataType.Geo
        }
      }
    }
    let optimizationInfo: TableOptimizationInfo | null = null
    if (optimizationCol || params[SF_SEARCH_OPTIMIZATION_COLUMN_PARAMETER_NAME]) {
      switch (provider) {
        case Provider.Postgres: {
          let pgCluster = params[PG_CLUSTERING_PARAMETER_NAME]
          if (typeof pgCluster !== 'boolean') {
            // Check: sometimes returning string <pgclustering>
            pgCluster = false
          }
          optimizationInfo = {
            column: optimizationCol,
            pgCluster: pgCluster as boolean
          }
          break
        }
        case Provider.Snowflake: {
          const searchOptimizationColName = params[SF_SEARCH_OPTIMIZATION_COLUMN_PARAMETER_NAME] as string
          let searchOptimizationCol: TableOptimizationColumn | null = null
          if (searchOptimizationColName) {
            searchOptimizationCol = {
              name: searchOptimizationColName,
              type: TableOptimizationColumnDataType.Geo
            }
          }
          optimizationInfo = {
            column: optimizationCol,
            sfSearchOptimizationColumn: searchOptimizationCol
          }
          break
        }
        default:
          optimizationInfo = {
            column: optimizationCol
          }
      }
    }
    return optimizationInfo
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { SaveAsTable }
